import React, { useState, useMemo, useCallback } from "react";
import TicketLogHeader from "../TicketLogTable/TicketLogHeader";
import { ChevronDown, ChevronRight } from "../../../../components/icons/svg/outline";

/**
 * TicketLogGroupByView - Grouped table view for ticket items
 * 
 * Displays tickets in a table grouped by a specific field (e.g., Project, Status).
 * Groups are collapsible with expand/collapse functionality.
 * Uses the same table structure and row components as the regular table view.
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of ticket objects to display
 * @param {Array} props.columns - Column configuration (same as table view)
 * @param {React.Component} props.RowComponent - Component to render each table row
 * @param {Function} props.onRowClick - Callback when a row is clicked
 * @param {string} props.groupBy - Field key to group by (e.g., 'job_number', 'status')
 * @param {string} [props.groupSortDirection='desc'] - Sort order for groups: 'asc' or 'desc'. Default 'desc' so job numbers (higher = newer) appear first.
 * @param {Function} props.getGroupLabel - Optional function to format group label
 * @param {Function} props.getGroupValue - Optional function to extract group value from item
 * @param {string} props.emptyMessage - Message when no items found
 * @param {Object} props.rowProps - Additional props passed to RowComponent
 * @param {string} [props.sortField] - Current sort field (sorts tickets within each group)
 * @param {string} [props.sortDirection] - 'asc' or 'desc'
 * @param {Function} [props.onSort] - Callback when column header is clicked
 */
const TicketLogGroupByView = ({
  data = [],
  columns = [],
  RowComponent,
  onRowClick,
  groupBy,
  groupSortDirection = 'desc',
  getGroupLabel,
  getGroupValue,
  emptyMessage = "No items found",
  rowProps = {},
  sortField,
  sortDirection,
  onSort,
}) => {
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Get group value from item
  const getGroupValueFromItem = useCallback((item) => {
    if (getGroupValue) {
      return getGroupValue(item);
    }
    // Default: extract value by key (supports nested keys)
    return groupBy.split('.').reduce((obj, key) => obj?.[key], item) ?? null;
  }, [getGroupValue, groupBy]);

  // Sort items within a group (same logic as TicketLogTable)
  const sortItemsWithinGroup = useCallback(
    (items) => {
      if (!sortField) return items;
      const sortableColumn = columns.find((col) => col.key === sortField);
      return [...items].sort((a, b) => {
        let aValue, bValue;
        if (sortableColumn?.getSortValue) {
          aValue = sortableColumn.getSortValue(a);
          bValue = sortableColumn.getSortValue(b);
        } else {
          aValue = sortField.split('.').reduce((obj, key) => obj?.[key], a) ?? "";
          bValue = sortField.split('.').reduce((obj, key) => obj?.[key], b) ?? "";
        }
        if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = (bValue || "").toLowerCase();
        }
        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    },
    [sortField, sortDirection, columns]
  );

  // Group data by the specified field and sort items within each group
  const groupedData = useMemo(() => {
    const groups = new Map();

    data.forEach((item) => {
      const groupValue = getGroupValueFromItem(item);
      const groupKey = groupValue ?? 'null'; // Use 'null' string for items without the field

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          value: groupValue,
          items: [],
        });
      }

      groups.get(groupKey).items.push(item);
    });

    const isDesc = groupSortDirection === 'desc';

    // Convert to array, sort each group's items, then sort groups
    const groupsArray = Array.from(groups.values());
    groupsArray.forEach((group) => {
      group.items = sortItemsWithinGroup(group.items);
    });

    return groupsArray.sort((a, b) => {
      // Sort null/undefined groups last
      if (a.value == null && b.value != null) return 1;
      if (a.value != null && b.value == null) return -1;
      if (a.value == null && b.value == null) return 0;

      const aNum = Number(a.value);
      const bNum = Number(b.value);
      const bothNumeric = !Number.isNaN(aNum) && !Number.isNaN(bNum);

      let cmp;
      if (bothNumeric) {
        cmp = aNum - bNum;
      } else {
        const aStr = String(a.value).toLowerCase();
        const bStr = String(b.value).toLowerCase();
        cmp = aStr.localeCompare(bStr);
      }
      return isDesc ? -cmp : cmp;
    });
  }, [data, getGroupValueFromItem, groupSortDirection, sortItemsWithinGroup]);

  // Toggle group expansion
  const toggleGroup = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  // Expand all groups by default
  React.useEffect(() => {
    if (expandedGroups.size === 0 && groupedData.length > 0) {
      const allKeys = new Set(groupedData.map(g => g.key));
      setExpandedGroups(allKeys);
    }
  }, [groupedData, expandedGroups.size]);

  // Get visible columns: exclude the group-by column (e.g. Project when grouped by job)
  const visibleColumns = useMemo(
    () => columns.filter((col) => !col.hidden && col.key !== groupBy),
    [columns, groupBy]
  );

  // Get formatted group label
  const getFormattedGroupLabel = (group) => {
    if (getGroupLabel) {
      return getGroupLabel(group.value, group.items);
    }
    
    // Default label format
    if (group.value == null) {
      return 'No ' + (groupBy.replace('_', ' ') || 'Group');
    }
    
    return String(group.value);
  };

  if (data.length === 0) {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <TicketLogHeader
            columns={visibleColumns}
            sortField={sortField ?? null}
            sortDirection={sortDirection ?? null}
            onSort={onSort ?? (() => {})}
          />
          <tbody className="bg-white divide-y divide-gray-200">
            <tr>
              <td
                colSpan={visibleColumns.length}
                className="px-6 py-4 text-center text-gray-500"
              >
                <i>{emptyMessage}</i>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <TicketLogHeader
          columns={visibleColumns}
          sortField={sortField ?? null}
          sortDirection={sortDirection ?? null}
          onSort={onSort ?? (() => {})}
        />
        <tbody className="bg-white divide-y divide-gray-200">
          {groupedData.map((group) => {
            const isExpanded = expandedGroups.has(group.key);
            const groupLabel = getFormattedGroupLabel(group);

            return (
              <React.Fragment key={group.key}>
                {/* Group Header Row */}
                <tr className="bg-gray-50 hover:bg-gray-100">
                  <td
                    colSpan={visibleColumns.length}
                    className="px-4 py-2"
                  >
                    <button
                      onClick={() => toggleGroup(group.key)}
                      className="flex items-center gap-2 w-full text-left font-medium text-gray-700 hover:text-gray-900"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                      <span className="flex-1">
                        {groupLabel}
                      </span>
                      <span className="text-sm text-gray-500 font-normal">
                        ({group.items.length} {group.items.length === 1 ? 'item' : 'items'})
                      </span>
                    </button>
                  </td>
                </tr>

                {/* Group Items */}
                {isExpanded &&
                  group.items.map((item) => (
                    <RowComponent
                      key={item.id}
                      item={item}
                      columns={visibleColumns}
                      onRowClick={onRowClick}
                      {...rowProps}
                    />
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TicketLogGroupByView;
